<template>
  <div class="category-specific-fields">
    <q-form @submit.prevent>
      <!-- Grouped Fields (when category mode is enabled) -->
      <div v-if="useCategorySystem && hasFieldGroups">
        <q-expansion-item
          v-for="(group, groupKey) in fieldGroupsData"
          :key="groupKey"
          :label="group.label"
          :caption="`${completedFieldsInGroup(groupKey, group)} / ${totalFieldsInGroup(group)} fields`"
          default-opened
          header-class="bg-grey-2"
          class="q-mb-md"
        >
          <q-card>
            <q-card-section>
              <div class="row q-col-gutter-md">
                <div
                  v-for="(field, fieldKey) in group.fields"
                  v-show="shouldShowField(field)"
                  :key="fieldKey"
                  :class="getFieldColumnClass(field.type)"
                >
                  <component
                    :is="getFieldComponent(field.type)"
                    v-bind="getFieldProps(fieldKey, field)"
                    @update:model-value="updateFieldValue(fieldKey, $event)"
                  />
                </div>
              </div>
            </q-card-section>
          </q-card>
        </q-expansion-item>
      </div>

      <!-- Standard Fields (when category mode is disabled or no groups) -->
      <div v-else class="row q-col-gutter-md">
        <div
          v-for="field in fields"
          v-show="shouldShowField(field)"
          :key="field.key"
          :class="getFieldColumnClass(field.type)"
        >
          <!-- Text Input -->
          <q-input
            v-if="field.type === 'text'"
            v-model="localValue[field.key]"
            :label="field.label"
            :hint="field.hint"
            outlined
            @update:model-value="emitUpdate"
          />

          <!-- Number Input -->
          <q-input
            v-else-if="field.type === 'number'"
            v-model.number="localValue[field.key]"
            :label="field.label"
            :hint="field.hint"
            type="number"
            outlined
            @update:model-value="emitUpdate"
          />

          <!-- Select Dropdown -->
          <q-select
            v-else-if="field.type === 'select'"
            v-model="localValue[field.key]"
            :label="field.label"
            :hint="field.hint"
            :options="field.options || []"
            outlined
            emit-value
            map-options
            @update:model-value="emitUpdate"
          />

          <!-- Multi-Select -->
          <q-select
            v-else-if="field.type === 'multi_select'"
            v-model="localValue[field.key]"
            :label="field.label"
            :hint="field.hint"
            :options="field.options || []"
            outlined
            multiple
            emit-value
            map-options
            @update:model-value="emitUpdate"
          />

          <!-- Boolean Toggle -->
          <q-toggle
            v-else-if="field.type === 'boolean'"
            v-model="localValue[field.key]"
            :label="field.label"
            @update:model-value="emitUpdate"
          />

          <!-- JSON Array (for lists like zones served) -->
          <div v-else-if="field.type === 'json_array'" class="q-gutter-sm">
            <div class="text-subtitle2">{{ field.label }}</div>
            <q-chip
              v-for="(item, index) in localValue[field.key] || []"
              :key="index"
              removable
              color="primary"
              text-color="white"
              @remove="removeArrayItem(field.key, index)"
            >
              {{ item }}
            </q-chip>
            <div class="row q-gutter-sm">
              <q-input
                v-model="newArrayItem[field.key]"
                :placeholder="`Add ${field.label}`"
                outlined
                dense
                class="col"
                @keyup.enter="addArrayItem(field.key)"
              />
              <q-btn
                icon="add"
                color="primary"
                dense
                @click="addArrayItem(field.key)"
              />
            </div>
          </div>

          <!-- Textarea -->
          <q-input
            v-else-if="field.type === 'textarea'"
            v-model="localValue[field.key]"
            :label="field.label"
            :hint="field.hint"
            type="textarea"
            :rows="field.rows || 4"
            outlined
            @update:model-value="emitUpdate"
          />

          <!-- Date Input -->
          <q-input
            v-else-if="field.type === 'date'"
            v-model="localValue[field.key]"
            :label="field.label"
            :hint="field.hint"
            outlined
            @update:model-value="emitUpdate"
          >
            <template #append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-date v-model="localValue[field.key]" @update:model-value="emitUpdate">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Close" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>
      </div>
    </q-form>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { QInput, QSelect, QToggle } from 'quasar';

  const props = defineProps({
    category: {
      type: String,
      required: true,
    },
    fields: {
      type: [Array, Object],
      required: true,
    },
    modelValue: {
      type: Object,
      default: () => ({}),
    },
    useCategorySystem: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['update:modelValue']);

  const localValue = ref({});
  const newArrayItem = ref({});

  /**
   * Apply default values from field definitions to local value
   */
  const applyDefaults = (currentValue) => {
    const result = { ...currentValue };

    // Handle grouped fields (object with groups)
    if (typeof props.fields === 'object' && !Array.isArray(props.fields)) {
      Object.values(props.fields).forEach((group) => {
        if (group.fields) {
          Object.entries(group.fields).forEach(([fieldKey, field]) => {
            if (field.default !== undefined && (result[fieldKey] === undefined || result[fieldKey] === null || result[fieldKey] === '')) {
              result[fieldKey] = field.default;
            }
          });
        }
      });
    }
    // Handle flat fields (array)
    else if (Array.isArray(props.fields)) {
      props.fields.forEach((field) => {
        if (field.default !== undefined && (result[field.key] === undefined || result[field.key] === null || result[field.key] === '')) {
          result[field.key] = field.default;
        }
      });
    }

    return result;
  };

  // Initialize local value from model value with defaults
  onMounted(() => {
    localValue.value = applyDefaults(props.modelValue);
  });

  // Watch modelValue changes
  watch(() => props.modelValue, (newVal) => {
    localValue.value = applyDefaults(newVal);
  }, { deep: true });

  // Watch fields changes - re-apply defaults when field definitions load/change
  watch(() => props.fields, () => {
    localValue.value = applyDefaults(localValue.value);
    emitUpdate(); // Emit so parent gets the defaults
  }, { deep: true });

  const emitUpdate = () => {
    emit('update:modelValue', { ...localValue.value });
  };

  const getFieldColumnClass = (type) => {
    // Textareas and JSON arrays take full width
    if (type === 'textarea' || type === 'json_array') {
      return 'col-12';
    }
    // Booleans take quarter width
    if (type === 'boolean') {
      return 'col-12 col-md-3';
    }
    // Default to half width
    return 'col-12 col-md-6';
  };

  const addArrayItem = (fieldKey) => {
    const value = newArrayItem.value[fieldKey];
    if (!value || !value.trim()) {return;}

    if (!localValue.value[fieldKey]) {
      localValue.value[fieldKey] = [];
    }

    localValue.value[fieldKey].push(value.trim());
    newArrayItem.value[fieldKey] = '';
    emitUpdate();
  };

  const removeArrayItem = (fieldKey, index) => {
    if (localValue.value[fieldKey]) {
      localValue.value[fieldKey].splice(index, 1);
      emitUpdate();
    }
  };

  // Field Grouping Support
  const hasFieldGroups = computed(() => {
    return typeof props.fields === 'object' && !Array.isArray(props.fields);
  });

  const fieldGroupsData = computed(() => {
    if (hasFieldGroups.value) {
      return props.fields;
    }
    return {};
  });

  const totalFieldsInGroup = (group) => {
    if (!group || !group.fields) {
      return 0;
    }
    // Only count visible fields
    return Object.values(group.fields).filter((field) => shouldShowField(field)).length;
  };

  const completedFieldsInGroup = (groupKey, group) => {
    if (!group || !group.fields) {
      return 0;
    }

    let completed = 0;
    Object.entries(group.fields).forEach(([fieldKey, field]) => {
      // Only count visible fields
      if (!shouldShowField(field)) {
        return;
      }
      const value = localValue.value[fieldKey];
      if (value !== null && value !== undefined && value !== '') {
        completed++;
      }
    });

    return completed;
  };

  const getFieldComponent = (type) => {
    switch (type) {
      case 'text':
      case 'number':
      case 'textarea':
      case 'date':
        return QInput;
      case 'select':
      case 'multi_select':
        return QSelect;
      case 'boolean':
        return QToggle;
      default:
        return QInput;
    }
  };

  const getFieldProps = (fieldKey, field) => {
    const baseProps = {
      modelValue: localValue.value[fieldKey],
      label: field.label + (field.required ? ' *' : ''),
      hint: field.hint || (field.unit ? `Unit: ${field.unit}` : ''),
      placeholder: field.placeholder || '',
      outlined: true,
      dense: true,
    };

    // Add validation rules for required fields
    if (field.required) {
      baseProps.rules = [(val) => !!val || 'This field is required'];
    }

    switch (field.type) {
      case 'number':
        return {
          ...baseProps,
          type: 'number',
        };
      case 'textarea':
        return {
          ...baseProps,
          type: 'textarea',
          rows: field.rows || 4,
        };
      case 'date':
        return {
          ...baseProps,
          // Date-specific props can be added here
        };
      case 'select':
        return {
          ...baseProps,
          options: field.options || [],
          emitValue: true,
          mapOptions: true,
        };
      case 'multi_select':
        return {
          ...baseProps,
          options: field.options || [],
          multiple: true,
          emitValue: true,
          mapOptions: true,
        };
      case 'boolean':
        return {
          modelValue: localValue.value[fieldKey],
          label: field.label,
        };
      default:
        return baseProps;
    }
  };

  const updateFieldValue = (fieldKey, value) => {
    localValue.value[fieldKey] = value;
    emitUpdate();
  };

  /**
   * Check if a field should be shown based on show_when conditions
   * @param {Object} field - Field definition with optional show_when property
   * @returns {boolean} - Whether the field should be visible
   */
  const shouldShowField = (field) => {
    // If no show_when condition, always show
    if (!field.show_when) {
      return true;
    }

    // Check each condition in show_when
    for (const [conditionKey, conditionValue] of Object.entries(field.show_when)) {
      const currentValue = localValue.value[conditionKey];

      // If condition value is an array, check if current value is in the array
      if (Array.isArray(conditionValue)) {
        if (!conditionValue.includes(currentValue)) {
          return false;
        }
      } else {
        // Single value comparison
        if (currentValue !== conditionValue) {
          return false;
        }
      }
    }

    return true;
  };

  /**
   * Count visible fields in a group (for accurate completion display)
   */
  const visibleFieldsInGroup = (group) => {
    if (!group || !group.fields) {
      return 0;
    }
    return Object.values(group.fields).filter((field) => shouldShowField(field)).length;
  };
</script>

<style scoped>
.category-specific-fields {
  width: 100%;
}
</style>
